import useLogout from "api/UserResource/useLogout";
import { ButtonLoader } from "components";
import React, { useState, useEffect } from "react";
import { BsBuilding } from "react-icons/bs";
import { FiBell, FiCreditCard, FiLock, FiMail, FiUser, FiMenu, FiX, FiPlus } from "react-icons/fi";
import { HiHeart, HiChartBar } from "react-icons/hi";
import { NavLink } from "react-router-dom";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import avatar from "assets/images/avatar.jpg";
import { AiOutlineDollarCircle, AiOutlineLoading3Quarters } from "react-icons/ai";
import { SiGooglemaps } from "react-icons/si";
import { Permission } from "utils/server-constants/permissions";
import DeleteAccount from "./DeleteAccount";
import { motion, AnimatePresence } from "framer-motion";
import useCheckIfUserIsSubscribed from "api/SubscriptionsResource/useCheckIfUserIsSubscribed";
import AddSeatsModal from "components/AddSeatsModal";

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} props.className
 * @param {boolean} props.isLoading
 * @param {Object} props.profile
 * @param {string} props.profile.first_name
 * @param {string} props.profile.last_name
 * @param {string} props.profile.email
 * @param {string} props.profile.avatar
 * @param {string} props.profile.avatar
 */
const AccountLayout = (props) => {
	const { children, isLoading, profile, showProfile = true } = props;

	const { hasRoles, hasPermissions } = useAuthStore();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [showAddSeatsModal, setShowAddSeatsModal] = useState(false);

	const iconSize = 20;

	const { mutateAsync, isLoading: isLoggingOut } = useLogout();
	const { data: isSubscribed } = useCheckIfUserIsSubscribed();

	const currentSubscription = isSubscribed?.data || null;
	const isInstitutional = currentSubscription?.category === "institution";
	const canAddSeats = isInstitutional && hasRoles([Role.Institution, Role.Educator]);

	// Handle escape key to close mobile menu
	useEffect(() => {
		const handleEscape = (event) => {
			if (event.key === "Escape" && isMobileMenuOpen) {
				setIsMobileMenuOpen(false);
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [isMobileMenuOpen]);

	const handleLogout = async () => {
		await mutateAsync();
	};

	const links = [
		{
			label: "Profile",
			path: "/my-account",
			icon: <FiUser size={iconSize} />,
			qs: "My profile",
			show: true,
		},
		{
			label: "Emails",
			path: "/my-account/emails",
			icon: <FiMail size={iconSize} />,
			qs: "Emails",
			show: true,
		},
		{
			label: "Password & authentication",
			path: "/my-account/password",
			icon: <FiLock size={iconSize} />,
			qs: "Password and authentication",
			show: true,
		},
		{
			label: "Subscriptions",
			path: "/my-account/subscriptions",
			icon: <FiCreditCard size={iconSize} />,
			qs: "My subscription",
			show: true,
		},
		{
			label: "Wishlist",
			path: "/my-account/wishlist",
			icon: <HiHeart size={iconSize} />,
			qs: "My wishlist",
			show: true,
		},
		{
			label: "Reading statistics",
			path: "/my-account/reading-stats",
			icon: <HiChartBar size={iconSize} />,
			qs: "Reading statistics",
			show: true,
		},
		{
			label: "Notification preferences",
			path: "/my-account/notification-preferences",
			icon: <FiBell size={iconSize} />,
			qs: "Notification preferences",
			show: true,
		},
		{
			label: "My Orders",
			path: "/my-account/orders",
			icon: <AiOutlineDollarCircle size={iconSize} />,
			qs: "My orders",
			show: true,
		},

		{
			label: "Assigned Orders",
			path: "/my-account/assigned-orders",
			icon: <AiOutlineDollarCircle size={iconSize} />,
			qs: "My assigned orders",
			show: hasPermissions([Permission.Courier]),
		},

		{
			label: "Addresses",
			path: "/my-account/my-addresses",
			icon: <SiGooglemaps size={iconSize} />,
			qs: "My addresses",
			show: true,
		},
		{
			label: "Publisher details",
			path: "/my-account/publisher",
			icon: <BsBuilding size={iconSize} />,
			qs: "Publisher details",
			show: hasRoles([Role.SeniorPublisher]),
		},
		{
			label: "Institution details",
			path: "/my-account/institution",
			icon: <BsBuilding size={iconSize} />,
			qs: "Institution details",
			show: hasRoles([Role.Institution]),
		},

		// FIXME Uncomment the comment
		// {
		// 	label: "Auto-deductions",
		// 	path: "/my-account/auto-deductions",
		// 	icon: <FiCreditCard size={iconSize} />,
		// 	qs: "Auto-deductions",
		// 	show: true,
		// },
	];

	return (
		<div className="flex flex-col lg:flex-row gap-6 min-h-screen">
			{/* Mobile Menu Button - Only visible on mobile */}
			<div className="lg:hidden mb-4">
				<motion.button
					onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
					className="flex items-center justify-between w-full px-4 py-3 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-700 hover:bg-gray-50 transition-colors duration-200"
					// whileHover={{ scale: 1.02 }}
					whileTap={{ scale: 0.98 }}
					transition={{ type: "spring", stiffness: 400, damping: 17 }}
				>
					<div className="flex items-center gap-3">
						<motion.div
							animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
							transition={{ duration: 0.2 }}
						>
							<FiMenu size={20} />
						</motion.div>
						<span className="font-medium">Account Menu</span>
					</div>
					<motion.div
						animate={{ rotate: isMobileMenuOpen ? 180 : 0 }}
						transition={{ duration: 0.2 }}
					>
						{isMobileMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
					</motion.div>
				</motion.button>
			</div>

			{/* Mobile Navigation Overlay */}
			<AnimatePresence>
				{isMobileMenuOpen && (
					<motion.div
						className="lg:hidden fixed inset-0 z-50"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
						onClick={() => setIsMobileMenuOpen(false)}
					>
						{/* Backdrop */}
						<motion.div
							className="absolute inset-0 bg-black bg-opacity-50"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						/>

						{/* Menu Panel - Slides from top */}
						<motion.div
							className="fixed top-0 left-0 right-0 bg-white shadow-xl"
							initial={{ y: "-100%", opacity: 0 }}
							animate={{ y: 45, opacity: 1 }}
							exit={{ y: "-100%", opacity: 0 }}
							transition={{
								type: "spring",
								damping: 25,
								stiffness: 200,
								duration: 0.3,
							}}
							onClick={(e) => e.stopPropagation()}
						>
							<div className="p-4 max-h-[85vh] overflow-y-auto">
								{/* Mobile Profile Section */}
								<motion.div
									initial={{ opacity: 0, y: -20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.1, duration: 0.3 }}
								>
									{showProfile && !isLoading && (
										<div className="mb-6 pb-6 border-b border-gray-100 flex items-center gap-3 justify-between">
											<div className="flex items-center gap-3">
												<div className="relative">
													<img
														src={profile?.avatar || avatar}
														alt="Profile"
														className="h-12 w-12 rounded-full border-2 border-gray-100 object-cover"
													/>
													<div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
												</div>
												<div className="flex-1 min-w-0">
													<h3 className="font-bold text-gray-900 truncate text-sm">
														{profile?.first_name} {profile?.last_name}
													</h3>
													<p className="text-gray-500 text-xs truncate">{profile?.email}</p>
												</div>
											</div>

											<button onClick={() => setIsMobileMenuOpen(false)}>
												<FiX size={20} />
											</button>
										</div>
									)}
								</motion.div>

								{/* Mobile Navigation Links */}
								<motion.nav
									className="space-y-2"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ delay: 0.2, duration: 0.3 }}
								>
									{links
										.filter((link) => link.show)
										.map((link, index) => (
											<div key={index}>
												<NavLink
													exact
													to={link.path + (link.qs ? `?heading=${link.qs}` : "")}
													className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ease-in-out hover:bg-gray-50 hover:text-akgreen group"
													activeClassName="bg-akgreen text-white shadow-sm"
													onClick={() => setIsMobileMenuOpen(false)}
												>
													<span className="text-current group-hover:scale-110 transition-transform duration-200">
														{link.icon}
													</span>
													<span className="text-sm font-medium">{link.label}</span>
												</NavLink>
											</div>
										))}
								</motion.nav>

								{/* Mobile Add Seats Button - Only for institutional subscriptions */}
								{canAddSeats && (
									<div className="mt-4">
										<button
											onClick={() => {
												setShowAddSeatsModal(true);
												setIsMobileMenuOpen(false);
											}}
											className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-akgreen to-akgreener text-white hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm font-semibold"
										>
											<FiPlus size={18} />
											Add Seats
										</button>
									</div>
								)}

								{/* Mobile Logout Button */}
								<div className="mt-6 pt-6 border-t border-gray-100">
									<button
										type="button"
										onClick={handleLogout}
										className="w-full px-4 py-3 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transform duration-200 hover:scale-105 flex items-center justify-center gap-2 text-sm font-medium"
									>
										Logout
										{isLoggingOut && (
											<AiOutlineLoading3Quarters size={16} className="animate-spin" />
										)}
									</button>
								</div>

								<div className="mt-3">
									<DeleteAccount />
								</div>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Desktop Vertical Sidebar Navigation - Hidden on mobile */}
			<div className="hidden lg:block w-80 flex-shrink-0">
				<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sticky top-24">
					{/* Profile Section */}
					{showProfile && !isLoading && (
						<div className="mb-6 pb-6 border-b border-gray-100">
							<div className="flex items-center gap-3">
								<div className="relative">
									<img
										src={profile?.avatar || avatar}
										alt="Profile"
										className="h-12 w-12 rounded-full border-2 border-gray-100 object-cover"
									/>
									<div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
								</div>
								<div className="flex-1 min-w-0">
									<h3 className="font-bold text-gray-900 truncate text-sm">
										{profile?.first_name} {profile?.last_name}
									</h3>
									<p className="text-gray-500 text-xs truncate">{profile?.email}</p>
								</div>
							</div>
						</div>
					)}

					{/* Navigation Links */}
					<nav className="space-y-2">
						{links
							.filter((link) => link.show)
							.map((link, index) => (
								<NavLink
									exact
									key={index}
									to={link.path + (link.qs ? `?heading=${link.qs}` : "")}
									className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ease-in-out hover:bg-gray-50 hover:text-akgreen group"
									activeClassName="bg-akgreen text-white shadow-sm"
								>
									<span className="text-current group-hover:scale-110 transition-transform duration-200">
										{link.icon}
									</span>
									<span className="text-sm font-medium">{link.label}</span>
								</NavLink>
							))}
					</nav>

					{/* Add Seats Button - Only for institutional subscriptions */}
					{canAddSeats && (
						<div className="mt-4">
							<button
								onClick={() => setShowAddSeatsModal(true)}
								className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-akgreen to-akgreener text-white hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm font-semibold"
							>
								<FiPlus size={18} />
								Add Seats
							</button>
						</div>
					)}

					{/* Logout Button */}
					<div className="mt-6 pt-6 border-t border-gray-100">
						<button
							type="button"
							onClick={handleLogout}
							className="w-full px-4 py-3 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transform duration-200 hover:scale-105 flex items-center justify-center gap-2 text-sm font-medium"
						>
							Logout
							{isLoggingOut && <AiOutlineLoading3Quarters size={16} className="animate-spin" />}
						</button>
					</div>

					<div className="mt-3">
						<DeleteAccount />
					</div>
				</div>
			</div>

			{/* Main Content Area */}
			<div className="flex-1 min-w-0">
				{(function () {
					if (isLoading) {
						return (
							<div className="flex justify-center items-center py-12">
								<ButtonLoader size={60} />
							</div>
						);
					}

					return (
						<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 lg:p-8">
							{children}
						</div>
					);
				})()}
			</div>

			{/* Add Seats Modal */}
			{canAddSeats && (
				<AddSeatsModal
					visible={showAddSeatsModal}
					onHide={() => setShowAddSeatsModal(false)}
					subscription={currentSubscription}
				/>
			)}
		</div>
	);
};

export default AccountLayout;
